import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Breadcrumbs, type Crumb } from '@/components/ui/Breadcrumbs';
import { useSettingsSections } from './settingsNav';
import { cn } from '@/lib/cn';

/**
 * Shell for every settings screen: a trail showing where you are, a persistent
 * section list, and the active page beside it.
 *
 * The point is orientation. Previously each CRM config page was a standalone
 * route with no indication that ten siblings existed, and personal preferences
 * lived on a different page entirely with no link between them.
 *
 * Deliberately no `<h1>` here — every child page already owns its title and
 * header actions, so adding one would stack two headings on every screen.
 */
export function SettingsLayout() {
  const sections = useSettingsSections();
  const { pathname } = useLocation();

  const active = sections
    .flatMap((section) => section.items.map((item) => ({ ...item, section })))
    .filter((item) => (item.exact ? pathname === item.path : pathname.startsWith(item.path)))
    // Longest match wins so `/settings/support/routing` beats `/settings/support`.
    .sort((a, b) => b.path.length - a.path.length)[0];

  const crumbs: Crumb[] = [{ label: 'Settings', to: '/settings' }];
  if (active) {
    crumbs.push({ label: active.section.label });
    crumbs.push({ label: active.label });
  }

  return (
    <>
      <Breadcrumbs items={crumbs} className="mb-3" />

      <div className="flex flex-col gap-6 lg:flex-row">
        <SettingsSubNav sections={sections} />
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </>
  );
}

function SettingsSubNav({ sections }: { sections: ReturnType<typeof useSettingsSections> }) {
  return (
    <nav
      aria-label="Settings sections"
      /*
       * Horizontal scroller on small screens, sticky rail from `lg` up. Twenty
       * entries stacked vertically above the content would push the actual
       * settings off a phone screen.
       */
      className="lg:sticky lg:top-6 lg:h-fit lg:w-56 lg:shrink-0"
    >
      <div className="-mx-3 flex gap-5 overflow-x-auto px-3 pb-2 lg:mx-0 lg:flex-col lg:gap-4 lg:overflow-visible lg:px-0 lg:pb-0">
        {sections.map((section) => (
          <div key={section.id} className="shrink-0 lg:shrink">
            <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {section.label}
            </p>
            <ul className="flex gap-1 lg:flex-col lg:gap-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.exact}
                      className={({ isActive }) =>
                        cn(
                          'press flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring',
                          isActive && 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
                        )
                      }
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}
